import logger from '../../utils/logger';
import React, { useState } from "react";
import { toast } from "react-toastify";
import "../../style/ImagingModals.css";

const listOfRegions = [
  "Head & Neck",
  "Spine",
  "Upper Extremity",
  "Lower Extremity",
  "Chest / Thorax",
  "Abdomen / Pelvis",
  "Vascular",
  "Soft Tissue / Foreign Body",
  "Full Body",
];

/**
 * Modal for adding a new body part to a fixed region.
 *
 * Props:
 *  - isOpen      {boolean}   Whether the modal is visible
 *  - onClose     {function}  Called when the modal should close
 *  - onSuccess   {function}  Called after a successful save (triggers refetchCatalog)
 */
const AddBodyPartModal = ({ isOpen, onClose, onSuccess, user = null }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [region, setRegion] = useState("");
  const [bodyPartName, setBodyPartName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const resetForm = () => {
    setRegion("");
    setBodyPartName("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!region) {
      toast.warning("Please select a region");
      return;
    }
    if (!bodyPartName.trim()) {
      toast.warning("Please enter a body part name");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/customBodyPart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ region, name: bodyPartName.trim(), user }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.info("Body part submitted for admin approval");
        resetForm();
        onSuccess?.();
        onClose();
      } else {
        toast.error(data.msg || "Failed to add body part");
      }
    } catch (error) {
      logger.error("Error adding body part:", error);
      toast.error("Error adding body part. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="im-modal-overlay" onClick={handleClose}>
      <div className="im-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <h3>Add Body Part</h3>
          <button className="im-modal-close" onClick={handleClose} disabled={isSubmitting}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="im-modal-body">
            <div className="im-form-group">
              <label htmlFor="bp-region">Region</label>
              <select
                id="bp-region"
                className="im-select"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select Region</option>
                {listOfRegions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="im-form-group">
              <label htmlFor="bp-name">Body Part Name</label>
              <input
                id="bp-name"
                type="text"
                className="im-input"
                value={bodyPartName}
                onChange={(e) => setBodyPartName(e.target.value)}
                placeholder="e.g. Clavicle"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="im-modal-footer">
            <button
              type="button"
              className="im-btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="im-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Body Part"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBodyPartModal;
